import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { CheckCircleIcon, ArrowRightIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../config';

// console.log('Mounting Payment');

export default function Payment() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const mounted = useRef(true);

  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnPath = searchParams.get('return_path') || '/onboarding';

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const error = searchParams.get('error');
    const canceled = searchParams.get('canceled');
    if (error) {
      setError(`Payment error: ${error}`);
    } else if (canceled) {
      setError('Payment was canceled. You can try again when you\'re ready.');
    }
  }, [searchParams]);

  const handleCheckout = async () => {
    if (isSubmitting) return;
    if (mounted.current) {
      setError('');
      setIsSubmitting(true);
    }
    try {
      if (!user) {
        throw new Error("Please sign in to complete your purchase");
      }
      localStorage.setItem('paymentReturnPath', returnPath);
      const response = await fetch(`${API_BASE_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId: 'price_1RJFgdKio1YMvzIRs5ndmHR7',
          userId: user.id,
          email: user.email,
          successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}&return_path=${encodeURIComponent(returnPath)}`,
          cancelUrl: `${window.location.origin}/payment?return_path=${encodeURIComponent(returnPath)}&canceled=true`,
        }),
      });
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error('Invalid server response');
      }
      if (!response.ok) {
        if (data.fallbackUrl) {
          window.location.href = data.fallbackUrl;
          return;
        }
        throw new Error(data.error || 'Failed to initialize payment');
      }
      if (!data?.url) {
        throw new Error('No checkout URL returned');
      }
      try {
        localStorage.setItem('checkoutIntent', JSON.stringify({
          timestamp: new Date().toISOString(),
          userId: user.id,
          sessionId: data.sessionId,
          returnPath
        }));
        if (data.sessionId) {
          localStorage.setItem('stripe_session_id', data.sessionId);
        }
      } catch (storageErr) {
        // Non-critical error, continue without storing
      }
      window.location.href = data.url;
    } catch (error) {
      if (mounted.current) {
        setError(error.message || 'Failed to initialize checkout. Please try again.');
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <h2 className="text-2xl font-bold text-white mb-6">Activate Your Membership</h2>
      {error && (
        <div className="mb-6 p-3 bg-red-500/20 border border-red-500 rounded-md text-red-300 text-sm">
          {error}
        </div>
      )}
      <div className="bg-gray-900 rounded-lg p-6 border border-gray-800 mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-xl font-bold text-white">Annual Membership</h3>
            <div className="mt-1 flex items-center">
              <span className="text-primary font-bold text-xl">£120</span>
              <span className="text-gray-400 text-sm ml-1">/year</span>
            </div>
          </div>
        </div>
        <ul className="mt-6 space-y-4">
          <li className="flex items-start">
            <CheckCircleIcon className="h-5 w-5 text-primary flex-shrink-0 mr-3 mt-0.5" />
            <div>
              <span className="text-white font-medium">Instant access to 10+ culture playbooks</span>
              <p className="text-gray-400 text-sm mt-1">Worth £600 — yours immediately</p>
            </div>
          </li>
          <li className="flex items-start">
            <CheckCircleIcon className="h-5 w-5 text-primary flex-shrink-0 mr-3 mt-0.5" />
            <div>
              <span className="text-white font-medium">Regular playbook drops</span>
              <p className="text-gray-400 text-sm mt-1">The only way to get Open Org's latest playbooks</p>
            </div>
          </li>
          <li className="flex items-start">
            <CheckCircleIcon className="h-5 w-5 text-primary flex-shrink-0 mr-3 mt-0.5" />
            <div>
              <span className="text-white font-medium">£30,000+ in exclusive HR Tech & Services discounts</span>
              <p className="text-gray-400 text-sm mt-1">Member-only curated deals</p>
            </div>
          </li>
        </ul>
        <div className="mt-8">
          <button
            onClick={handleCheckout}
            disabled={isSubmitting}
            className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
          >
            {isSubmitting ? 'Activating Your Membership...' : 'Unlock Full Access • £120/year'}
            {!isSubmitting && <ArrowRightIcon className="ml-2 h-5 w-5" />}
          </button>
        </div>
      </div>
      <div className="text-center space-y-3">
        <div className="flex items-center justify-center space-x-3">
          <LockClosedIcon className="h-5 w-5 text-gray-400" />
          <span className="text-gray-400">Secure payments powered by Stripe</span>
        </div>
        <p className="text-sm text-gray-400">
          Your payment is secured using SSL encryption. You can cancel your subscription at any time.
        </p>
      </div>
    </div>
  );
} 